import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import {
  cacheOwner,
  getCached,
  setCached,
  invalidateCache,
  CACHE_KEYS,
} from "../utils/sessionCache";
import {
  getTnStatus,
  connectTnEnVentana,
  abrirVentanaOAuth,
} from "../lib/tiendaNube";
import { loadOnboardingSeen, isSeen, markSeen } from "../lib/onboardingSeen";
import { fetchHotSaleCoupon } from "../lib/hotSaleCoupon";
import { estadoDeCreditos } from "../lib/creditsStatus";
import { esPlanUltra } from "../lib/checkoutPlans";
import { useEsMobile } from "./useEsMobile";

/** Cuál de los banners promocionales de home corresponde mostrar (o ninguno). */
export type HomeBannerKind =
  /* Acá estaba "upsell", el de la prueba gratis. Lo reemplazó la barra superior de
     créditos — ADR-2026-08-03-barra-creditos-reemplaza-upsell. */
  | "capacitacion"
  | "brand"
  | "cuestionario"
  | "escritorio"
  | "tiendanube"
  | null;

/** Estado de la suscripción que amerita un banner informativo en la home. */
export type SubscriptionStatus = "cancelled" | "paused" | null;

export type HomeBannersState = {
  /**
   *  - "capacitacion": usuario de plan ULTRA que todavía no descartó ni agendó la
   *    llamada de capacitación incluida. PRIMERO en la cadena, antes que "brand"
   *    (ADR-2026-08-25-banner-llamada-capacitacion-ultra).
   *  - "brand": usuario pago sin análisis de marca (`config.media_agent_analysis`).
   *  - "cuestionario": usuario pago que todavía no respondió la encuesta de
   *    feedback (`company_profiles.cuestionario_respondido`).
   *  - "escritorio": usuario pago con la encuesta ya respondida, EN TELÉFONO
   *    (<640px) y que no descartó el banner — invitación a usar Atlas en una
   *    computadora. Único `kind` que depende del dispositivo.
   *  - "tiendanube": usuario pago CON análisis hecho, sin Tienda Nube conectada
   *    y que no descartó el banner (post "Mejorá tus fotos").
   *  - null: no corresponde ninguno (o aún cargando).
   */
  kind: HomeBannerKind;
  /**
   * Saldo de créditos (`imagenes_restantes`). Se expone para **todos** los
   * planes, no sólo el gratis: el aviso de "sin créditos" / "se te están por
   * acabar" vale para cualquiera (ADR-2026-07-30-solo-criterio-saldo-toda-app).
   */
  freeImagesRemaining: number | null;
  /**
   * Capacidad del plan (`max_fotos_mes`), el denominador del umbral del 5%. Ver
   * `lib/creditsStatus`.
   */
  creditsTotal: number | null;
  /** Empresa del usuario — la necesita el modal del cuestionario. */
  idCompany: string | null;
  /**
   * País del usuario === Argentina. Ya NO cambia el copy del upsell (ADR-2026-07-30-banner-home-habla-descuento-real:
   * el banner habla del descuento real, que es el mismo para todos); queda
   * porque es contexto barato de tener resuelto.
   */
  isArgentina: boolean;
  /**
   * Descuento vigente en % entero (ej. `50`), del mismo cupón invisible que
   * aplica el catálogo (`lib/hotSaleCoupon`). `null` si no hay cupón activo — y
   * entonces el banner NO promete ningún descuento. Solo se resuelve para
   * "upsell". Ver ADR-2026-07-30-banner-home-habla-descuento-real.
   */
  discountPercent: number | null;
  /**
   * Estado de la suscripción (INDEPENDIENTE de `kind`):
   *  - "cancelled": hay una cancelación programada a futuro.
   *  - "paused": la suscripción está pausada (reanuda a futuro).
   *  - null: ninguno.
   */
  status: SubscriptionStatus;
  /** Fecha de cancelación formateada (solo si status === "cancelled"). */
  cancelDate: string | null;
  /** Fecha de reanudación formateada (solo si status === "paused"). */
  resumeDate: string | null;
  /**
   * Motivo de la cancelación (`company_profiles.trigger_cancelacion`). Solo
   * relevante si status === "cancelled". Valor conocido: "fondos_insuficientes"
   * (el último pago fue rechazado por falta de fondos) → la UI muestra un copy
   * distinto (por rechazo de pago, con reintento automático). `null` = baja
   * voluntaria u otro motivo (copy default).
   */
  cancelTrigger: string | null;
  /**
   * `true` cuando el hook ya sabe qué banner corresponde (aunque la respuesta sea
   * "ninguno") — ADR-2026-08-03-home-aparece-entera-o-skeleton. Opcional porque el
   * estado interno no lo lleva: lo agrega el hook al devolver, igual que los tres
   * callbacks de abajo.
   */
  resuelto?: boolean;
  /** Descarta el banner de la llamada de capacitación Ultra — por usuario, en la
   *  DB (`onboarding_seen`). Lo llaman "No me interesa" Y "Agendar": las dos
   *  acciones dan el cartel por resuelto, como el de Tienda Nube. */
  dismissCapacitacion?: () => void;
  /** Descarta el banner de Tienda Nube ("No me interesa") — por usuario, en la DB
   *  (`onboarding_seen`). Lo inyecta el hook; el render lo llama desde el botón. */
  dismissTiendanube?: () => void;
  /** Conecta Tiendanube (OAuth popup) sobre la marca default y oculta el banner
   *  al éxito. Lanza si el user cancela / popup bloqueado (el banner queda). */
  connectTiendanube?: () => Promise<void>;
  /** Descarta el banner "Probá Atlas en tu computadora" ("No me interesa" o
   *  cerrar la pantalla) — por usuario, en la DB (`onboarding_seen`) — y reevalúa
   *  la cadena para que Tienda Nube aparezca en el acto. */
  dismissEscritorio?: () => void;
  /** Oculta el banner del cuestionario tras responderlo. El flag real
   *  (`cuestionario_respondido`) lo escribe el webhook de premio, así que acá
   *  solo actualizamos el estado local para que el banner se vaya en el acto. */
  dismissCuestionario?: () => void;
};

/* Acá vivía `MIN_IMAGENES_UPSELL`: cuántas imágenes tenía que generar un usuario de
   prueba gratis para ver el banner de "suscribite a un plan". `principal` lo había
   bajado de 5 a 3 (ADR-2026-08-03-upsell-de-home-a-las-3-imagenes) porque con 5 el
   aviso llegaba con la mitad de la prueba ya gastada. Se va con el banner
   (ADR-2026-08-05-la-barra-de-creditos-le-gana-al-upsell): quien avisa ahora es la
   barra de créditos, que se dispara por SALDO y no por cantidad generada — o sea,
   antes todavía. */

const EMPTY: HomeBannersState = {
  kind: null,
  freeImagesRemaining: null,
  creditsTotal: null,
  idCompany: null,
  isArgentina: false,
  discountPercent: null,
  status: null,
  cancelDate: null,
  resumeDate: null,
  cancelTrigger: null,
};

// Mismo formato que el Paywall ("20 de julio de 2026").
const formatDate = (raw: unknown): string | null => {
  if (!raw) return null;
  const d = new Date(raw as string);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("es-AR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/**
 * Capacidad por plan para el fallback de dev (ADR-2026-07-30-panel-dev-plan-creditos-generadas y ADR-2026-07-30-solo-criterio-saldo-toda-app): sin empresa no hay
 * `max_fotos_mes` que leer, y sin denominador el umbral del 5% no se puede
 * calcular. Son los valores del catálogo real.
 */
const isFuture = (raw: unknown): boolean => {
  if (!raw) return false;
  const d = new Date(raw as string);
  return !isNaN(d.getTime()) && d.getTime() > Date.now();
};

/**
 * Resuelve qué banner de home mostrar, con las MISMAS condiciones que la
 * pantalla clásica de selección de productos. Fuente única para que el clásico
 * y la Interfaz V2 muestren exactamente los mismos banners.
 *
 * Banners promocionales (`kind`, mutuamente excluyentes). El orden de esta
 * lista ES la prioridad: el primero que aplica gana y corta la evaluación.
 *  - Plan gratis → NINGUNO: su aviso es la barra de créditos, no un banner
 *    (ADR-2026-08-05-la-barra-de-creditos-le-gana-al-upsell).
 *  - Plan ULTRA + sin descartar/agendar → "capacitacion" (agendá tu llamada de
 *    capacitación incluida). PRIMERO de todos, incluso antes que "brand": es una
 *    prestación ya paga del plan y agendarla vale más cuanto más cerca del alta
 *    (ADR-2026-08-25-banner-llamada-capacitacion-ultra).
 *  - Plan pago + sin `media_agent_analysis` → "brand" (describir la marca),
 *    salvo que `suppressBrand` esté activo.
 *  - Plan pago + encuesta sin responder → "cuestionario". Va después de
 *    "brand" porque describir la marca mejora lo que la IA genera (más
 *    urgente para el usuario) y antes de "tiendanube", que es el menos
 *    apremiante de los tres. Los tres son una SECUENCIA: al completar uno
 *    aparece el siguiente (ADR-889).
 *  - Plan pago + encuesta respondida + EN TELÉFONO → "escritorio" (probá Atlas
 *    en tu computadora). Va justo detrás de la encuesta y sólo abajo de 640px:
 *    en una compu el mensaje no aplica, así que ahí la cadena lo saltea y sigue
 *    a "tiendanube" (ADR-2026-08-25-cartel-probar-atlas-en-escritorio).
 *
 * Estado de suscripción (`status`, ortogonal a `kind`):
 *  - Cancelación programada a futuro → "cancelled".
 *  - Reanudación programada a futuro (pausa) → "paused".
 * El consumidor decide si renderizar el banner de estado (ver `HomeBannersView`
 * con `showStatus`); hoy solo se muestra en mobile.
 *
 * @param opts.suppressBrand fuerza ocultar el banner de marca (ej. al volver de
 *   `/redes-sociales/descripcion-brand` con `hideBrandBanner`).
 */
export function useHomeBanners(opts?: {
  suppressBrand?: boolean;
}): HomeBannersState {
  const suppressBrand = opts?.suppressBrand ?? false;
  /**
   * Corte de teléfono (<640px) para el banner "escritorio". Es reactivo, así que
   * cruzar el breakpoint reevalúa la cadena: es lo que hace falta para que rotar
   * el teléfono o achicar la ventana no deje colgado un cartel que ya no aplica.
   */
  const esMobile = useEsMobile();
  const [state, setState] = useState<HomeBannersState>(EMPTY);
  const uidRef = useRef<string | null>(null);
  const companyRef = useRef<string | null>(null);
  /**
   * Contador para reevaluar la cadena de banners sin recargar la página
   * (ADR-889). Lo bumpea `dismissCuestionario`: al responder la encuesta, el
   * paso siguiente —Tienda Nube— tiene que aparecer ahí mismo, porque es el
   * final de una secuencia y no un banner suelto.
   */
  const [revision, setRevision] = useState(0);
  /**
   * `false` hasta que la primera evaluación termina —
   * ADR-2026-08-03-home-aparece-entera-o-skeleton.
   *
   * `kind: null` es ambiguo: significa "no va ningún banner" Y "todavía no sé". El
   * consumidor no podía distinguirlos, así que dibujaba la home sin banner y lo
   * metía a mitad de camino, empujando todo lo de abajo. Con esto puede esperar.
   *
   * NO vuelve a `false` en las reevaluaciones (`revision`, overrides de dev): esas
   * corren con la home ya dibujada y apagarla sería un parpadeo peor que el salto.
   */
  const [resuelto, setResuelto] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Sin usuario o sin empresa el hook no tiene nada que leer y se va sin pintar banner.
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;
        uidRef.current = user.id;

        const { data: profile } = await supabase
          .from("profiles")
          .select("id_company")
          .eq("id", user.id)
          .single();
        const idCompany = profile?.id_company as string | undefined;
        if (!idCompany) return;
        companyRef.current = idCompany;

        const { data: company } = await supabase
          .from("company_profiles")
          .select(
            // `created_at` salió del select junto con el requisito de antigüedad
            // de la encuesta (ADR-889): ya no lo mira nadie.
            // `paid_plan_start_date`: ¿ya fue cliente pago? Decide si el banner puede
            // prometer el descuento — ver más abajo.
            "plan, pais, imagenes_restantes, max_fotos_mes, fecha_cancelacion_programada, fecha_reactivacion_programada, trigger_cancelacion, cuestionario_respondido, paid_plan_start_date",
          )
          .eq("id_company", idCompany)
          .single();
        if (cancelled) return;

        const plan = ((company as any)?.plan || "gratis") as string;
        const pais = ((company as any)?.pais || "") as string;
        const imagenesRestantes =
          typeof (company as any)?.imagenes_restantes === "number"
            ? ((company as any).imagenes_restantes as number)
            : null;
        // Denominador del umbral del 5% (ADR-2026-07-30-solo-criterio-saldo-toda-app).
        const capacidadPlan =
          typeof (company as any)?.max_fotos_mes === "number"
            ? ((company as any).max_fotos_mes as number)
            : null;
        const isArgentina = pais.toLowerCase() === "argentina";
        const isPaidPlan = plan && plan !== "gratis";

        // Estado de la suscripción (ortogonal al banner promocional). Solo
        // aplica a usuarios pagos; un free no tiene cancelación/pausa.
        const cancelRaw = (company as any)?.fecha_cancelacion_programada;
        const resumeRaw = (company as any)?.fecha_reactivacion_programada;
        const triggerRaw = (company as any)?.trigger_cancelacion;
        let status: SubscriptionStatus = null;
        let cancelDate: string | null = null;
        let resumeDate: string | null = null;
        let cancelTrigger: string | null = null;
        if (isPaidPlan && isFuture(cancelRaw)) {
          status = "cancelled";
          cancelDate = formatDate(cancelRaw);
          cancelTrigger = (triggerRaw as string) || null;
        } else if (isPaidPlan && isFuture(resumeRaw)) {
          status = "paused";
          resumeDate = formatDate(resumeRaw);
        }

        if (!isPaidPlan) {
          /* Acá se contaba cuántas imágenes había generado el usuario: el banner
             `upsell` salía a partir de N (ADR-680, bajado a 3 en
             ADR-2026-08-03-upsell-de-home-a-las-3-imagenes). Ese banner ya no existe
             (ADR-2026-08-03-barra-creditos-reemplaza-upsell) y la barra que lo
             reemplaza NO mira las generadas sino el SALDO, así que el `count` se fue
             con él — era una consulta por carga de home que ya no decide nada.

             Decisión de merge (ADR-2026-08-05-la-barra-de-creditos-le-gana-al-upsell):
             `principal` venía de mejorar ese conteo —umbral a 3 y el arreglo del
             filtro `has_error` NULL— y esa mejora se pierde acá, a sabiendas. No es
             que el conteo estuviera mal: es que ya nadie pregunta por él. La barra se
             dispara por saldo bajo, que llega ANTES que las 3 imágenes y no necesita
             una consulta extra. Si alguna vez vuelve un aviso por cantidad generada,
             el filtro correcto es `or("has_error.is.null,has_error.eq.false")` y NO
             `neq("has_error", true)`: en SQL `has_error <> true` da NULL cuando la
             columna es NULL, así que esas filas quedaban fuera del conteo. */
          // El descuento se consulta sólo si la barra va a mostrarse —o sea, si el
          // saldo NO está ok— y sale del mismo cupón que aplica el catálogo: si acá
          // dijéramos un % fijo y el cupón estuviera vencido o con otro
          // multiplicador, prometería un precio que el checkout no cobra
          // (ADR-2026-07-30-banner-home-habla-descuento-real).
          //
          // Y ojo: `plan = 'gratis'` NO alcanza para saber que le corresponde el
          // descuento. Un ex cliente que se dio de baja queda en `gratis` y ya no
          // lleva la promo de captación (ADR-2026-08-18-descuento-no-revive-al-volver-a-gratis):
          // sin este filtro la barra le prometía un 30% que ni el catálogo muestra
          // ni el servidor de pagos cobra. Con `null` cae sola en su copy sin descuento.
          const hayAvisoDeSaldo =
            estadoDeCreditos(imagenesRestantes, capacidadPlan) !== "ok";
          const yaFueClientePago = !!(company as any)?.paid_plan_start_date;
          const cupon =
            hayAvisoDeSaldo && !yaFueClientePago ? await fetchHotSaleCoupon() : null;
          if (cancelled) return;
          setState({
            // Sin cartel propio: el aviso de la prueba gratis es la barra de saldo.
            kind: null,
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: cupon
              ? Math.round((1 - cupon.multiplicador) * 100)
              : null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        // Plan ULTRA: la llamada de capacitación incluida, PRIMERO en la cadena
        // (ADR-2026-08-25-banner-llamada-capacitacion-ultra) — también por encima
        // de `suppressBrand`, que sólo habla del banner de marca. El flag por
        // usuario lo marcan tanto "No me interesa" como "Agendar" (una vez que
        // tocó cualquiera de los dos, insistir es ruido). `loadOnboardingSeen` es
        // idempotente: para los no-Ultra se sigue cargando más abajo, como antes.
        if (esPlanUltra(plan)) {
          await loadOnboardingSeen(user.id);
          if (cancelled) return;
          if (!isSeen("capacitacion_ultra_banner")) {
            setState({
              kind: "capacitacion",
              freeImagesRemaining: imagenesRestantes,
              creditsTotal: capacidadPlan,
              idCompany,
              isArgentina,
              discountPercent: null,
              status,
              cancelDate,
              resumeDate,
              cancelTrigger,
            });
            return;
          }
        }

        // Plan pago: banner de marca si todavía no hay análisis de marca.
        if (suppressBrand) {
          setState({
            kind: null,
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }
        let hasAnalysis = getCached<boolean>(CACHE_KEYS.COMPANY_CONFIG);
        if (hasAnalysis === null) {
          try {
            const { data: config } = await supabase
              .from("config")
              .select("media_agent_analysis")
              .eq("id_company", idCompany)
              .maybeSingle();
            let computed = !!config?.media_agent_analysis;
            // Fallback robusto: `media_agent_analysis` puede quedar VACÍO ('')
            // aunque el user YA haya completado "Mejorá tus fotos" (el webhook
            // del análisis a veces devuelve vacío). En ese caso igual ocultamos
            // el banner si la marca DEFAULT ya tiene IDENTIDAD cargada (colores
            // o contexto) — así el banner es one-time real: desaparece cuando
            // la marca ya está completada, sin depender de ese flag poco fiable.
            if (!computed) {
              const { data: brand } = await supabase
                .from("brand_profiles")
                .select("colors, context")
                .eq("id_company", idCompany)
                .eq("is_default", true)
                .eq("is_draft", false)
                .limit(1)
                .maybeSingle();
              const colors = (brand as { colors?: unknown } | null)?.colors;
              const ctx = (brand as { context?: unknown } | null)?.context;
              const hasIdentity =
                (Array.isArray(colors) && colors.length > 0) ||
                (typeof ctx === "string" && ctx.trim().length > 0);
              if (hasIdentity) computed = true;
            }
            hasAnalysis = computed;
            // `user.id` y NO `uidRef.current`: el ref es mutable y compartido —
            // leerlo después del `await` daría el usuario NUEVO y la guarda no
            // filtraría nada. `user` es const de esta corrida del efecto.
            setCached(CACHE_KEYS.COMPANY_CONFIG, hasAnalysis, user.id);
          } catch {
            hasAnalysis = false;
          }
        }
        if (cancelled) return;
        if (!hasAnalysis) {
          setState({
            kind: "brand",
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        // Cuestionario (encuesta de feedback, +10 imágenes de premio): plan
        // pago + todavía sin responder. Antes esta condición vivía suelta en
        // `SeleccionarProductosAnuncio` y su banner se renderizaba ENCIMA de
        // este componente, así que podía salir junto con el de marca o el de
        // Tienda Nube. Al ser un `kind` más, la exclusión mutua es
        // estructural. Ver ADR-681.
        //
        // Ya NO se pide antigüedad de la empresa. Pedir ≥2 semanas partía la
        // secuencia justo para quien recién se suscribe: completaba la marca y
        // el paso siguiente se salteaba, cayendo directo en Tienda Nube; la
        // encuesta recién aparecía dos semanas después, fuera de contexto. El
        // orden pedido es marca → encuesta → Tienda Nube, uno detrás del otro
        // (ADR-889).
        const cuestionarioRespondido =
          (company as any)?.cuestionario_respondido === true;
        if (!cuestionarioRespondido) {
          setState({
            kind: "cuestionario",
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        // Los dos carteles que quedan se descartan por usuario contra
        // `profiles.onboarding_seen`, así que se carga UNA vez acá (es
        // idempotente) y los dos leen del mismo cache.
        await loadOnboardingSeen(user.id);
        if (cancelled) return;

        // "Probá Atlas en tu computadora" — SÓLO en teléfono, justo después de la
        // encuesta (ADR-2026-08-25-cartel-probar-atlas-en-escritorio).
        //
        // El filtro de dispositivo está ACÁ y no en una clase `sm:hidden` del
        // render: `kind` es uno solo y el primero que aplica corta la cadena, así
        // que un cartel que ganara en desktop y después se escondiera por CSS
        // dejaría a esos usuarios con un hueco invisible y sin volver a ver el de
        // Tienda Nube nunca. Salteándolo en el hook, en una compu la cadena sigue
        // igual que antes.
        if (esMobile && !isSeen("probar_escritorio_banner")) {
          setState({
            kind: "escritorio",
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        // Análisis de marca YA hecho → evaluar el banner de Tienda Nube (aparece
        // justo donde antes no quedaba nada, post "Mejorá tus fotos"). Se muestra
        // si el user NO lo descartó ("No me interesa", por usuario) y la empresa
        // NO tiene ninguna tienda conectada.
        if (isSeen("tiendanube_banner")) {
          setState({
            kind: null,
            freeImagesRemaining: imagenesRestantes,
            creditsTotal: capacidadPlan,
            idCompany,
            isArgentina,
            discountPercent: null,
            status,
            cancelDate,
            resumeDate,
            cancelTrigger,
          });
          return;
        }

        let tnConnected = getCached<boolean>(CACHE_KEYS.TN_STATUS);
        if (tnConnected === null) {
          try {
            const { data: brandRow } = await supabase
              .from("brand_profiles")
              .select("id")
              .eq("id_company", idCompany)
              .eq("is_default", true)
              .eq("is_draft", false)
              .limit(1)
              .maybeSingle();
            const bId = (brandRow as { id?: string } | null)?.id ?? null;
            const st = await getTnStatus(bId);
            // `companyHasStores`: si CUALQUIER marca de la empresa ya tiene tienda
            // conectada, no ofrecemos conectar (es por empresa, no por marca).
            tnConnected = st.connected || st.companyHasStores;
            setCached(CACHE_KEYS.TN_STATUS, tnConnected, user.id);
          } catch {
            tnConnected = false;
          }
        }
        if (cancelled) return;
        if (tnConnected) {
          // Ya conectó una tienda → marcamos el flag "resuelto" (mismo que
          // "No me interesa") para que el banner NO vuelva nunca, ni si después
          // desconecta. En la próxima carga `isSeen` corta antes (sin consultar
          // el estado de conexión).
          markSeen("tiendanube_banner", uidRef.current);
        }
        setState({
          kind: tnConnected ? null : "tiendanube",
          freeImagesRemaining: imagenesRestantes,
          creditsTotal: capacidadPlan,
          idCompany,
          isArgentina,
          discountPercent: null,
          status,
          cancelDate,
          resumeDate,
          cancelTrigger,
        });
      } catch {
        // Si la consulta falla (sesión vencida, permisos) la home queda sin banner.
        if (!cancelled) setState(EMPTY);
      }
    })().finally(() => {
      // Un solo lugar para avisar "ya sé qué banner va" — el cuerpo de arriba tiene
      // cuatro salidas (sin usuario, sin empresa, éxito, error) y marcarlo en cada
      // una es olvidárselo en la quinta. Ver `resuelto` en el tipo del estado.
      if (!cancelled) setResuelto(true);
    });
    return () => {
      cancelled = true;
    };
  }, [suppressBrand, revision, esMobile]);

  /**
   * Cartel de la llamada de capacitación Ultra, resuelto — lo llaman "No me
   * interesa" Y "Agendar" (ADR-2026-08-25-banner-llamada-capacitacion-ultra).
   * Marca el flag por usuario (DB), baja el cartel ya y **reevalúa la cadena**
   * para que el paso siguiente (marca / encuesta / …) aparezca en el acto —
   * mismo criterio que `dismissEscritorio` (ADR-889).
   */
  const dismissCapacitacion = useCallback(() => {
    markSeen("capacitacion_ultra_banner", uidRef.current);
    setState((s) => ({ ...s, kind: null }));
    setRevision((r) => r + 1);
  }, []);

  // "No me interesa": marca el flag por usuario (DB) y oculta el banner ya. En la
  // próxima carga, `isSeen("tiendanube_banner")` lo mantiene oculto.
  const dismissTiendanube = useCallback(() => {
    markSeen("tiendanube_banner", uidRef.current);
    setState((s) => ({ ...s, kind: null }));
  }, []);

  // "Conectar": mismo flujo OAuth que Mi marca (popup + espera),
  // sobre la marca DEFAULT (igual que el chequeo que decide mostrar el banner).
  // Al éxito: cache TN a "conectada", flag resuelto (el banner no vuelve) y
  // ocultar ya. Si lanza (cancelado / popup bloqueado), el banner queda.
  const connectTiendanube = useCallback(async () => {
    const idCompany = companyRef.current;
    if (!idCompany) throw new Error("Empresa no disponible.");
    // ⚠️ La ventana del OAuth se abre ACÁ, sincrónicamente dentro del click y
    // ANTES del `await` que busca la marca default: Safari (y todos los
    // navegadores de iOS) la bloquean si se la pide después de una espera —
    // ver ADR-2026-08-07-tiendanube-popup-safari.
    const popup = abrirVentanaOAuth();
    // Dueño de la sesión ANTES de los awaits (la marca default + toda la vuelta
    // del OAuth, que puede tardar). Si al volver cambió, el `setCached` de abajo
    // se descarta solo en vez de contarle a otra cuenta que tiene Tienda Nube.
    const duenio = cacheOwner();
    let bId: string | null = null;
    try {
      const { data: brandRow } = await supabase
        .from("brand_profiles")
        .select("id")
        .eq("id_company", idCompany)
        .eq("is_default", true)
        .eq("is_draft", false)
        .limit(1)
        .maybeSingle();
      bId = (brandRow as { id?: string } | null)?.id ?? null;
    } catch (e) {
      try {
        popup?.close(); // no dejar la ventana de carga colgada
      } catch {
        /* noop */
      }
      throw e;
    }
    await connectTnEnVentana(bId, popup);
    setCached(CACHE_KEYS.TN_STATUS, true, duenio);
    markSeen("tiendanube_banner", uidRef.current);
    setState((s) => ({ ...s, kind: null }));
  }, []);

  /**
   * "No me interesa" del cartel de escritorio — y también cerrar la pantalla que
   * abre "Probar": una vez que el usuario leyó el mensaje, repetírselo es
   * insistir. Marca el flag por usuario (DB) y **reevalúa la cadena** para que
   * Tienda Nube, que es el paso siguiente, aparezca en el acto (mismo criterio
   * que `dismissCuestionario`, ADR-889).
   *
   * No invalida el cache de Tienda Nube: descartar este cartel no puede haber
   * cambiado si la empresa tiene o no una tienda conectada.
   */
  const dismissEscritorio = useCallback(() => {
    markSeen("probar_escritorio_banner", uidRef.current);
    setState((s) => ({ ...s, kind: null }));
    setRevision((r) => r + 1);
  }, []);

  /**
   * Encuesta respondida. El webhook de premio marca `cuestionario_respondido`
   * en la DB; acá se baja el banner y se **reevalúa la cadena** para que el paso
   * siguiente (Tienda Nube) aparezca en el momento, sin esperar a la próxima
   * carga de la home (ADR-889).
   *
   * Antes sólo bajaba el banner, para ahorrarse la consulta al vuelo. El costo
   * de eso era romper la secuencia justo en su último tramo: el usuario
   * completaba la encuesta y no pasaba nada visible.
   *
   * Se invalida el cache de sesión del estado de Tienda Nube: sin eso la
   * reevaluación releería el mismo valor cacheado.
   */
  const dismissCuestionario = useCallback(() => {
    setState((s) => ({ ...s, kind: null }));
    invalidateCache(CACHE_KEYS.TN_STATUS);
    setRevision((r) => r + 1);
  }, []);

  return {
    ...state,
    resuelto,
    dismissCapacitacion,
    dismissTiendanube,
    connectTiendanube,
    dismissEscritorio,
    dismissCuestionario,
  };
}
